/**
 * 3B-3（子供向け）: プロフィール選択サブステップ
 *
 * ステップ方式で1つずつ選択:
 * 1. 容姿（Selectドロップダウン）- 4項目すべて必須
 * 2. 好きな色（2段階選択: ベース色 → バリエーション）- 必須
 * 3. 得意な教科（4x3グリッド）- 1つ以上選択必須
 */
import { useRef, useState } from "react";
import I18n from "common/i18n";
import { usePlaySound } from "common/utils";
import { Select, SelectOption } from "common/elements/Select/Select";
import { COLOR_PALETTE, generateColorVariations, BaseColorKey } from "features/user/constants";
import {
  CreateUserState,
  UserType,
  FavoritesSubStep,
  AccessoryType,
  HairstyleType,
  BodyTypeType,
  FaceShapeType,
} from "../../types";

interface FavoritesStepProps {
  state: CreateUserState;
  updateState: (updates: Partial<CreateUserState>) => void;
  userType: UserType;
  favoritesSubStep: FavoritesSubStep;
}

/** 得意な教科選択肢 */
const BEST_SUBJECT_OPTIONS = [
  { key: "japanese", icon: "menu_book", labelKey: "MSG_SUBJECT_LANGUAGE" },
  { key: "math", icon: "calculate", labelKey: "MSG_SUBJECT_MATH" },
  { key: "science", icon: "science", labelKey: "MSG_SUBJECT_SCIENCE" },
  { key: "social", icon: "public", labelKey: "MSG_SUBJECT_SOCIAL" },
  { key: "music", icon: "music_note", labelKey: "MSG_SUBJECT_MUSIC" },
  { key: "art", icon: "palette", labelKey: "MSG_SUBJECT_ART" },
  { key: "pe", icon: "sports_soccer", labelKey: "MSG_SUBJECT_PE" },
  { key: "life", icon: "eco", labelKey: "MSG_SUBJECT_LIFE" },
  { key: "home_ec", icon: "restaurant", labelKey: "MSG_SUBJECT_HOME_EC" },
  { key: "ethics", icon: "volunteer_activism", labelKey: "MSG_SUBJECT_ETHICS" },
  { key: "english", icon: "translate", labelKey: "MSG_SUBJECT_ENGLISH" },
  { key: "integrated", icon: "hub", labelKey: "MSG_SUBJECT_INTEGRATED" },
  { key: "special_activities", icon: "groups", labelKey: "MSG_SUBJECT_SPECIAL_ACTIVITIES" },
];

/** 身につけるもの選択肢 */
const ACCESSORY_OPTIONS: { key: AccessoryType; icon: string; labelKey: string }[] = [
  { key: "none", icon: "block", labelKey: "MSG_ACCESSORY_NONE" },
  { key: "megane", icon: "eyeglasses", labelKey: "MSG_ACCESSORY_MEGANE" },
  { key: "boushi", icon: "checkroom", labelKey: "MSG_ACCESSORY_BOUSHI" },
  { key: "hairpin", icon: "auto_fix_high", labelKey: "MSG_ACCESSORY_HAIRPIN" },
  { key: "ribbon", icon: "redeem", labelKey: "MSG_ACCESSORY_RIBBON" },
  { key: "bandaid", icon: "healing", labelKey: "MSG_ACCESSORY_BANDAID" },
];

/** 髪型選択肢 */
const HAIRSTYLE_OPTIONS: SelectOption<HairstyleType>[] = [
  { value: "very_short", label: "MSG_HAIRSTYLE_VERY_SHORT" },
  { value: "short", label: "MSG_HAIRSTYLE_SHORT" },
  { value: "medium", label: "MSG_HAIRSTYLE_MEDIUM" },
  { value: "slightly_long", label: "MSG_HAIRSTYLE_SLIGHTLY_LONG" },
  { value: "long", label: "MSG_HAIRSTYLE_LONG" },
];

/** 体型選択肢 */
const BODY_TYPE_OPTIONS: SelectOption<BodyTypeType>[] = [
  { value: "thin", label: "MSG_BODY_TYPE_THIN" },
  { value: "slightly_thin", label: "MSG_BODY_TYPE_SLIGHTLY_THIN" },
  { value: "medium", label: "MSG_BODY_TYPE_MEDIUM" },
  { value: "slightly_chubby", label: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY" },
  { value: "chubby", label: "MSG_BODY_TYPE_CHUBBY" },
];

/** 顔の形選択肢 */
const FACE_SHAPE_OPTIONS: SelectOption<FaceShapeType>[] = [
  { value: "round", label: "MSG_FACE_SHAPE_ROUND" },
  { value: "oval", label: "MSG_FACE_SHAPE_OVAL" },
  { value: "oblong", label: "MSG_FACE_SHAPE_OBLONG" },
  { value: "square", label: "MSG_FACE_SHAPE_SQUARE" },
  { value: "triangle", label: "MSG_FACE_SHAPE_TRIANGLE" },
];

/** サブステップごとのタイトルを取得 */
const _getTitle = (subStep: FavoritesSubStep): string => {
  switch (subStep) {
    case "color":
      return I18n.t("MSG_PROFILE_AI_FAVORITE_COLOR");
    case "bestSubject":
      return I18n.t("MSG_PROFILE_AI_BEST_SUBJECT");
    case "appearance":
      return I18n.t("MSG_PROFILE_AI_APPEARANCE");
    default:
      return "";
  }
};

export const FavoritesStep = ({ state, updateState, favoritesSubStep }: FavoritesStepProps) => {
  const play = usePlaySound();

  // Select用のopen状態
  const [hairstyleOpen, setHairstyleOpen] = useState(false);
  const [bodyTypeOpen, setBodyTypeOpen] = useState(false);
  const [faceShapeOpen, setFaceShapeOpen] = useState(false);

  // Select用のanchorRef
  const hairstyleRef = useRef<HTMLDivElement>(null);
  const bodyTypeRef = useRef<HTMLDivElement>(null);
  const faceShapeRef = useRef<HTMLDivElement>(null);

  // ベース色選択ハンドラ（単一選択）
  const handleBaseColorSelect = (baseKey: BaseColorKey) => {
    play();
    // 単一選択：選択したベース色を設定、バリエーションはリセット
    updateState({
      selectedBaseColors: [baseKey],
      favoriteColors: [],
    });
  };

  // バリエーション選択ハンドラ（単一選択）
  const handleVariationSelect = (hexColor: string) => {
    play();
    updateState({ favoriteColors: [hexColor] });
  };

  const handleBestSubjectSelect = (subject: string) => {
    play();
    const current = state.bestSubjects || [];
    if (current.includes(subject)) {
      updateState({ bestSubjects: current.filter((s) => s !== subject) });
    } else {
      updateState({ bestSubjects: [...current, subject] });
    }
  };

  const handleAccessorySelect = (accessory: AccessoryType) => {
    play();
    const current = state.accessory || [];

    // 「なし」を選択した場合：他をすべて解除して「なし」のみにする
    if (accessory === "none") {
      if (current.includes("none")) {
        updateState({ accessory: [] });
      } else {
        updateState({ accessory: ["none"] });
      }
      return;
    }

    // 他のアクセサリーを選択した場合：「なし」を解除
    const withoutNone = current.filter((a) => a !== "none");
    if (withoutNone.includes(accessory)) {
      updateState({ accessory: withoutNone.filter((a) => a !== accessory) });
    } else {
      updateState({ accessory: [...withoutNone, accessory] });
    }
  };

  /** 色選択（1画面：上部=主色、下部=バリエーション） */
  const renderColorSelection = () => {
    const selectedBase = (state.selectedBaseColors || [])[0] || null;
    const variations = selectedBase ? generateColorVariations(selectedBase) : [];

    return (
      <div className="flex-col-el items-center gap-8">
        {/* 上部：主色選択 */}
        <div className="flex-row-el flex-center gap-6">
          {COLOR_PALETTE.map((option) => {
            const isActive = selectedBase === option.key;
            const isWhite = option.key === "white";
            return (
              <div
                key={option.key}
                onClick={() => handleBaseColorSelect(option.key)}
                className="flex-col-el flex-center gap-2 cursor-pointer select-none"
              >
                <div
                  className={`
                    w-[100px] h-[100px] rounded-full
                    active:scale-95
                    ${isActive ? "ring-4 ring-green-100" : ""}
                    ${isWhite ? "border-2 border-gray-40" : ""}
                  `}
                  style={{ backgroundColor: option.hex }}
                >
                  {isActive && (
                    <div className="flex-col-el flex-center w-full h-full">
                      <span className="material-symbols-outlined text-[40px] text-white drop-shadow-md">
                        check
                      </span>
                    </div>
                  )}
                </div>
                <span
                  className={`text-body text-[18px] ${isActive ? "text-green-100" : "text-textcolor-black/70"}`}
                >
                  {I18n.t(option.labelKey)}
                </span>
              </div>
            );
          })}
        </div>

        {/* 下部：バリエーション選択（常に場所を確保） */}
        <div className="flex-col-el items-center gap-4">
          <p className="text-body text-textcolor-black text-[20px]">
            {I18n.t("MSG_COLOR_SELECT_SHADE")}
          </p>
          <div className="flex-row-el gap-5">
            {selectedBase
              ? variations.map((hex, idx) => {
                  const isSelected = (state.favoriteColors || []).includes(hex);
                  return (
                    <div
                      key={idx}
                      onClick={() => handleVariationSelect(hex)}
                      className={`
                      flex-col-el flex-center
                      w-[80px] h-[80px]
                      rounded-full
                      cursor-pointer select-none
                      active:scale-95
                      ${isSelected ? "ring-4 ring-green-100" : ""}
                    `}
                      style={{ backgroundColor: hex }}
                    >
                      {isSelected && (
                        <span className="material-symbols-outlined text-[32px] text-white drop-shadow-md">
                          check
                        </span>
                      )}
                    </div>
                  );
                })
              : // プレースホルダー（5つの空の円）
                Array.from({ length: 5 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="flex-col-el flex-center w-[80px] h-[80px] rounded-full border-2 border-dashed border-gray-40"
                  />
                ))}
          </div>
        </div>
      </div>
    );
  };

  /** 得意な教科選択グリッド（複数選択） */
  const renderBestSubjectSelection = () => (
    <div className="grid grid-cols-4 gap-3 justify-items-center">
      {BEST_SUBJECT_OPTIONS.map((option) => {
        const isActive = (state.bestSubjects || []).includes(option.key);
        return (
          <div
            key={option.key}
            onClick={() => handleBestSubjectSelect(option.key)}
            className={`
              flex-col-el flex-center relative
              w-[120px] h-[120px]
              rounded-[8px]
              cursor-pointer select-none
              active:scale-95
              ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
            `}
          >
            {isActive && (
              <span className="absolute top-1 right-1 material-symbols-outlined text-[16px] text-green-100">
                check_circle
              </span>
            )}
            <span
              className={`material-symbols-outlined !text-[32px] mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
            >
              {option.icon}
            </span>
            <span
              className={`text-body text-[20px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
            >
              {I18n.t(option.labelKey)}
            </span>
          </div>
        );
      })}
    </div>
  );

  /** ラベル取得ヘルパー */
  const getOptionLabel = <T extends string>(
    options: SelectOption<T>[],
    value: T | null,
  ): string => {
    if (!value) return I18n.t("MSG_SELECT_PLACEHOLDER");
    const option = options.find((o) => o.value === value);
    return option ? I18n.t(option.label) : I18n.t("MSG_SELECT_PLACEHOLDER");
  };

  /** 容姿選択（Selectドロップダウン） */
  const renderAppearanceSelection = () => (
    <div className="flex-col-el items-center gap-4 w-full max-w-[700px]">
      {/* 身につけるもの（複数選択グリッド） */}
      <div className="flex-row-el items-center gap-4">
        <p className="text-body text-textcolor-black text-[24px] w-[180px] text-left">
          {I18n.t("MSG_PROFILE_AI_ACCESSORY")}
        </p>
        <div className="grid grid-cols-3 gap-2 w-[376px]">
          {ACCESSORY_OPTIONS.map((option) => {
            const isActive = (state.accessory || []).includes(option.key);
            return (
              <div
                key={option.key}
                onClick={() => handleAccessorySelect(option.key)}
                className={`
                  flex-col-el flex-center relative
                  w-[120px] h-[120px]
                  rounded-[8px]
                  cursor-pointer select-none
                  active:scale-95
                  ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
                `}
              >
                {isActive && (
                  <span className="absolute top-1 right-1 material-symbols-outlined text-[16px] text-green-100">
                    check_circle
                  </span>
                )}
                <span
                  className={`material-symbols-outlined !text-[32px] mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
                >
                  {option.icon}
                </span>
                <span
                  className={`text-body text-[20px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
                >
                  {I18n.t(option.labelKey)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 髪型 */}
      <div className="flex-row-el items-center gap-4">
        <p className="text-body text-textcolor-black text-[24px] w-[180px] text-left">
          {I18n.t("MSG_HAIRSTYLE_TITLE")}
        </p>
        <div
          ref={hairstyleRef}
          onClick={() => {
            play();
            setHairstyleOpen(true);
          }}
          className="flex-row-el items-center justify-between w-[376px] h-[56px] px-4 bg-white border-4 border-input-focus rounded-[8px] cursor-pointer select-none"
        >
          <span className="text-body text-[24px] text-textcolor-black">
            {getOptionLabel(HAIRSTYLE_OPTIONS, state.hairstyle)}
          </span>
          <span className="material-symbols-outlined !text-[24px]">expand_more</span>
        </div>
        <Select
          anchorRef={hairstyleRef}
          open={hairstyleOpen}
          onClose={() => setHairstyleOpen(false)}
          value={state.hairstyle}
          onChange={(v) => updateState({ hairstyle: v })}
          options={HAIRSTYLE_OPTIONS.map((o) => ({ ...o, label: I18n.t(o.label) }))}
        />
      </div>

      {/* 体型 */}
      <div className="flex-row-el items-center gap-4">
        <p className="text-body text-textcolor-black text-[24px] w-[180px] text-left">
          {I18n.t("MSG_BODY_TYPE_TITLE")}
        </p>
        <div
          ref={bodyTypeRef}
          onClick={() => {
            play();
            setBodyTypeOpen(true);
          }}
          className="flex-row-el items-center justify-between w-[376px] h-[56px] px-4 bg-white border-4 border-input-focus rounded-[8px] cursor-pointer select-none"
        >
          <span className="text-body text-[24px] text-textcolor-black">
            {getOptionLabel(BODY_TYPE_OPTIONS, state.bodyType)}
          </span>
          <span className="material-symbols-outlined !text-[24px]">expand_more</span>
        </div>
        <Select
          anchorRef={bodyTypeRef}
          open={bodyTypeOpen}
          onClose={() => setBodyTypeOpen(false)}
          value={state.bodyType}
          onChange={(v) => updateState({ bodyType: v })}
          options={BODY_TYPE_OPTIONS.map((o) => ({ ...o, label: I18n.t(o.label) }))}
        />
      </div>

      {/* 顔の形 */}
      <div className="flex-row-el items-center gap-4">
        <p className="text-body text-textcolor-black text-[24px] w-[180px] text-left">
          {I18n.t("MSG_FACE_SHAPE_TITLE")}
        </p>
        <div
          ref={faceShapeRef}
          onClick={() => {
            play();
            setFaceShapeOpen(true);
          }}
          className="flex-row-el items-center justify-between w-[376px] h-[56px] px-4 bg-white border-4 border-input-focus rounded-[8px] cursor-pointer select-none"
        >
          <span className="text-body text-[24px] text-textcolor-black">
            {getOptionLabel(FACE_SHAPE_OPTIONS, state.faceShape)}
          </span>
          <span className="material-symbols-outlined !text-[24px]">expand_more</span>
        </div>
        <Select
          anchorRef={faceShapeRef}
          open={faceShapeOpen}
          onClose={() => setFaceShapeOpen(false)}
          value={state.faceShape}
          onChange={(v) => updateState({ faceShape: v })}
          options={FACE_SHAPE_OPTIONS.map((o) => ({ ...o, label: I18n.t(o.label) }))}
        />
      </div>
    </div>
  );

  /** サブステップに応じたコンテンツ */
  const renderContent = () => {
    switch (favoritesSubStep) {
      case "color":
        return renderColorSelection();
      case "bestSubject":
        return renderBestSubjectSelection();
      case "appearance":
        return renderAppearanceSelection();
      default:
        return null;
    }
  };

  return (
    <div className="flex-col-el items-center justify-center w-full h-full overflow-y-auto">
      {/* コンテンツ */}
      {renderContent()}
    </div>
  );
};
